import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, PlusCircle, CheckCircle, Trash2, Pencil, FileText, X } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import api from '../../../../services/api';
import useAuth from '../../../../hooks/useAuth';

export default function TaskList() {
    const navigate = useNavigate();
    const { isAdmin } = useAuth();
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [completing, setCompleting] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [filter, setFilter] = useState('pending'); // 'pending' | 'completed' | 'all'

    // Quick Note State
    const [noteModal, setNoteModal] = useState({ show: false, task: null, notes: '' });
    const [savingNote, setSavingNote] = useState(false);

    const fetchTasks = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filter === 'pending') params.is_completed = false;
            if (filter === 'completed') params.is_completed = true;
            const res = await api.get('/tasks', { params });
            const data = res.data?.data || res.data || [];
            setTasks(data);
        } catch {
            toast.error('Failed to load tasks');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchTasks(); }, [filter]);

    const handleComplete = async (id) => {
        setCompleting(id);
        try {
            await api.patch(`/tasks/${id}/complete`);
            setTasks(prev => prev.map(t => t.id === id ? { ...t, is_completed: true } : t));
            toast.success('Task marked as complete');
        } catch {
            toast.error('Failed to complete task');
        } finally {
            setCompleting(null);
        }
    };

    const handleDelete = async (id) => {
        if (!isAdmin) return;
        if (!window.confirm('Delete this task?')) return;
        setDeleting(id);
        try {
            await api.delete(`/tasks/${id}`);
            setTasks(prev => prev.filter(t => t.id !== id));
            toast.success('Task deleted');
        } catch {
            toast.error('Failed to delete task');
        } finally {
            setDeleting(null);
        }
    };

    const handleQuickNote = async (e) => {
        e.preventDefault();
        if (!noteModal.task) return;
        setSavingNote(true);
        try {
            await api.post(`/tasks/${noteModal.task.id}/notes`, { content: noteModal.notes });
            toast.success('Progress note added');
            setNoteModal({ show: false, task: null, notes: '' });
            fetchTasks(); // Refresh to show updated counts/states if needed
        } catch {
            toast.error('Failed to add note');
        } finally {
            setSavingNote(false);
        }
    };

    return (
        <div>
            <Toaster position="top-right" richColors />
            <div className="max-w-5xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900">Tasks & Follow-Ups</h2>
                        <p className="text-sm text-gray-500 mt-1">Manage follow-up reminders</p>
                    </div>
                    <button onClick={() => navigate('/admin/tasks/new')} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition">
                        <PlusCircle className="w-4 h-4" /> Add Task
                    </button>
                </div>

                <div className="flex gap-2 mb-5">
                    {[['pending', 'Pending'], ['completed', 'Completed'], ['all', 'All']].map(([val, label]) => (
                        <button key={val} onClick={() => setFilter(val)} className={`px-4 py-1.5 rounded-lg text-sm font-medium border transition ${filter === val ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                            {label}
                        </button>
                    ))}
                </div>

                {loading ? (
                    <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 animate-spin text-blue-600" /></div>
                ) : (
                    <div className="bg-white shadow-sm rounded-xl border border-gray-200 overflow-hidden overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    {['Title', 'Due Date', 'Assigned To', 'Linked Lead', 'Notes', 'Status', 'Actions'].map(h => (
                                        <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {tasks.length === 0 ? (
                                    <tr><td colSpan={7} className="px-5 py-10 text-center text-gray-400">No tasks found</td></tr>
                                ) : tasks.map(t => (
                                    <tr key={t.id} className="hover:bg-gray-50">
                                        <td className="px-5 py-3 font-medium">
                                            <button onClick={() => navigate(`/admin/tasks/${t.id}`)} className="text-blue-600 hover:underline text-left">
                                                {t.title}
                                            </button>
                                        </td>
                                        <td className="px-5 py-3 text-sm text-gray-600">{t.due_date ? new Date(t.due_date).toLocaleDateString() : '—'}</td>
                                        <td className="px-5 py-3 text-sm text-gray-600">{t.assigned_user?.name || '—'}</td>
                                        <td className="px-5 py-3 text-sm">
                                            {t.lead ? (
                                                <button onClick={() => navigate(`/admin/leads/${t.lead.id}`)} className="text-blue-600 hover:underline">{t.lead.name}</button>
                                            ) : '—'}
                                        </td>
                                        <td className="px-5 py-3 text-sm text-gray-500 max-w-xs truncate cursor-pointer hover:text-gray-900" onClick={() => navigate(`/admin/tasks/${t.id}`)} title="View Progress History">
                                            {t.notes || 'No description'}
                                        </td>
                                        <td className="px-5 py-3">
                                            <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${t.is_completed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                                {t.is_completed ? 'Completed' : 'Pending'}
                                            </span>
                                        </td>
                                        <td className="px-5 py-3">
                                            <div className="flex gap-2">
                                                {!t.is_completed && (
                                                    <button onClick={() => handleComplete(t.id)} disabled={completing === t.id} className="p-1.5 text-green-600 hover:bg-green-50 rounded-md disabled:opacity-50" title="Mark Complete">
                                                        {completing === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                                                    </button>
                                                )}
                                                <button onClick={() => setNoteModal({ show: true, task: t, notes: t.notes || '' })} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-md" title="Quick Note">
                                                    <FileText className="w-4 h-4" />
                                                </button>
                                                <button onClick={() => navigate(`/admin/tasks/${t.id}/edit`)} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-md" title="Edit Task">
                                                    <Pencil className="w-4 h-4" />
                                                </button>
                                                {isAdmin && (
                                                    <button onClick={() => handleDelete(t.id)} disabled={deleting === t.id} className="p-1.5 text-red-600 hover:bg-red-50 rounded-md disabled:opacity-50">
                                                        {deleting === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Quick Note Modal */}
            {noteModal.show && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                    <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-bold text-gray-900">Update Note</h3>
                            <button onClick={() => setNoteModal({ show: false, task: null, notes: '' })} className="p-1 hover:bg-gray-100 rounded-full"><X className="w-5 h-5" /></button>
                        </div>
                        <p className="text-sm text-gray-500 mb-4 font-medium truncate">Task: {noteModal.task?.title}</p>
                        <form onSubmit={handleQuickNote}>
                            <textarea 
                                autoFocus
                                value={noteModal.notes}
                                onChange={e => setNoteModal(p => ({ ...p, notes: e.target.value }))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-black bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[120px]"
                                placeholder="Add notes here..."
                                style={{ color: '#000000', backgroundColor: '#ffffff' }}
                            />
                            <div className="flex gap-2 justify-end mt-4">
                                <button type="button" onClick={() => setNoteModal({ show: false, task: null, notes: '' })} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 rounded-lg">Cancel</button>
                                <button type="submit" disabled={savingNote} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2">
                                    {savingNote ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Note'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
